import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { adminPricingTierService } from '@/services/admin-pricing-tier.service'
import type {
  CreatePricingTierRequest,
  UpdatePricingTierRequest,
} from '@/types/pricing-tier'

/**
 * Query key factory for pricing tiers
 */
export const pricingTierKeys = {
  all: ['pricingTiers'] as const,
  lists: () => [...pricingTierKeys.all, 'list'] as const,
  list: (componentPriceId: string) =>
    [...pricingTierKeys.lists(), componentPriceId] as const,
  details: () => [...pricingTierKeys.all, 'detail'] as const,
  detail: (id: string) => [...pricingTierKeys.details(), id] as const,
}

/**
 * Hook to fetch list of pricing tiers for a component price
 *
 * @param componentPriceId - Component price ID to fetch tiers for
 * @returns Query result with tier list (sorted by tierOrder)
 */
export function usePricingTiers(componentPriceId: string) {
  return useQuery({
    queryKey: pricingTierKeys.list(componentPriceId),
    queryFn: () => adminPricingTierService.getTiers(componentPriceId),
    enabled: !!componentPriceId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
    select: (data) => {
      // Sort tiers by tierOrder for consistent display
      return [...data].sort((a, b) => a.tierOrder - b.tierOrder)
    },
  })
}

/**
 * Hook to create new pricing tier
 *
 * Automatically invalidates pricing tier list queries on success.
 *
 * @returns Mutation function and state
 */
export function useCreatePricingTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ componentPriceId, data }: {
      componentPriceId: string
      data: Omit<CreatePricingTierRequest, 'componentPriceId'>
    }) =>
      adminPricingTierService.createTier(componentPriceId, data),
    onSuccess: (createdTier, variables) => {
      // Invalidate tier list for this component price
      queryClient.invalidateQueries({
        queryKey: pricingTierKeys.list(variables.componentPriceId)
      })

      // Optionally set the created tier in cache
      queryClient.setQueryData(pricingTierKeys.detail(createdTier.id), createdTier)

      toast.success('Pricing tier created successfully', {
        description: createdTier.tierName
          ? `Tier "${createdTier.tierName}" has been created.`
          : `Tier ${createdTier.tierOrder} has been created.`,
      })
    },
    onError: (error: Error) => {
      toast.error('Failed to create pricing tier', {
        description: error.message || 'An unexpected error occurred',
      })
    },
  })
}

/**
 * Hook to update existing pricing tier
 *
 * Automatically invalidates pricing tier queries on success.
 *
 * @returns Mutation function and state
 */
export function useUpdatePricingTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePricingTierRequest }) =>
      adminPricingTierService.updateTier(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific tier detail
      queryClient.invalidateQueries({
        queryKey: pricingTierKeys.detail(variables.id),
      })

      // Invalidate all tier list queries
      queryClient.invalidateQueries({ queryKey: pricingTierKeys.lists() })

      toast.success('Pricing tier updated successfully', {
        description: 'The tier has been updated.',
      })
    },
    onError: (error: Error) => {
      toast.error('Failed to update pricing tier', {
        description: error.message || 'An unexpected error occurred',
      })
    },
  })
}

/**
 * Hook to delete pricing tier
 *
 * Automatically invalidates pricing tier queries on success.
 *
 * @returns Mutation function and state
 */
export function useDeletePricingTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tierId: string) => adminPricingTierService.deleteTier(tierId),
    onSuccess: (_, tierId) => {
      // Remove the tier from cache
      queryClient.removeQueries({
        queryKey: pricingTierKeys.detail(tierId),
      })

      // Invalidate all tier list queries
      queryClient.invalidateQueries({ queryKey: pricingTierKeys.lists() })

      toast.success('Pricing tier deleted successfully', {
        description: 'The tier has been permanently deleted.',
      })
    },
    onError: (error: Error) => {
      toast.error('Failed to delete pricing tier', {
        description: error.message || 'An unexpected error occurred',
      })
    },
  })
}
